import BannerComp from "../../components/BannerComp";
import FeatureCard from "../../components/FeatureCard";
import FeatureImageContent from "../../components/FeatureImageContent";
import ImageTextSection from "../../components/ImageTextSection";
import NumbersFlow from "../../components/NumbersFlow";
import ProPrograms from "../../components/ProPrograms";
import Seo from "../../components/Seo";
import {
  collectionSteps,
  featuresCardData,
  proProgramsData,
} from "../../data/marketing";
import { createBreadcrumbSchema, createWebPageSchema } from "../../lib/seo";

import section1Image from "./marketing_section1.webp";
import section2Image from "./marketing_section2.webp";
import section3Image from "./marketing_section3.webp";
import section4Image from "./marketing_section4.webp";

const seo = {
  title: "Marketing Services for Healthcare | MIT4Doctors",
  description:
    "We align our marketing with your systems, making it easier for patients to find, book, and pay for your services for a smooth, efficient process.",
  schema: [
    createWebPageSchema({
      path: "/services/marketing-services",
      title: "Marketing Services for Healthcare",
      description:
        "We will align our marketing with your systems. This makes it easier for patients to find, book, and pay for your services. It creates a smooth, efficient process for both your practice and your patients.",
    }),
    createBreadcrumbSchema(
      "/services/marketing-services",
      "Marketing Services for Healthcare",
    ),
  ],
};

export default function MarketingServices() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        canonicalPath="/services/marketing-services"
        schema={seo.schema}
      />
      <BannerComp
        heading="Marketing Services for Healthcare"
        paragraph="We will align our marketing with your systems. This makes it easier for patients to find, book, and pay for your services. It creates a smooth, efficient process for both your practice and your patients."
        centered
        BannerBgImg="/img/banners/marketing_hero.webp"
      />

      <ImageTextSection
        image={section1Image}
        imageAlt="Custom Website Development"
        imagePosition="left"
        title="Custom Website Development"
        description="Your website is often the first contact for potential patients. So, it's a crucial tool for your practice's success. We design custom, responsive websites. They reflect your practice's unique values and personality. They also provide an intuitive, continuous experience for your patients. Our websites simplify every step of the patient journey. They help with finding info, booking appointments, and paying online."
        highlightWords="Development"
      />

      <NumbersFlow
        eyebrow="Digital Growth Solutions"
        heading="Web Development for Medical Practices"
        highlights={["Web Development"]}
        intro="We build optimized healthcare websites that improve patient engagement, simplify payments, and increase online visibility."
        steps={collectionSteps}
      />

      <FeatureImageContent
        heading="Let’s Begin"
        description="Ready to take your practice to the next level? MITSI can help you. We can attract new patients, enhance engagement, and refine your processes. We do this with integrated digital marketing solutions."
        buttonLabel="Get Started"
        buttonUrl="/contact-us"
        imageAlt="Let’s Begin"
      />

      <FeatureCard
        sectionTitle="Virtual Assistant & Telemarketing Services"
        sectionDescription="Our Virtual Assistant and Telemarketing services grow the practice. They build strong ties with patients and referrers through constant communication and follow-up. How It Benefits You:"
        highlightText="Telemarketing Services"
        features={featuresCardData}
      />

      <ImageTextSection
        image={section2Image}
        imageAlt="Digital Ads Management"
        imagePosition="left"
        title="Digital Ads Management"
        description="Our Digital Ads Management services help patients find your practice. They are searching for healthcare services like yours. We specialize in ads on platforms like Google, Facebook, and Instagram. We reach the right audience at the right time. Our expert approach helps you stand out in a competitive market. It drives qualified leads to your practice."
        highlightWords="Management"
      />

      <ProPrograms
        heading="How Digital Ads Can Assist You"
        highlightText="Assist You"
        description="Digital advertising helps your healthcare practice reach the right patients, increase online visibility, and generate more qualified leads."
        programs={proProgramsData}
      />

      <ImageTextSection
        image={section3Image}
        imageAlt="Social Media Management"
        imagePosition="right"
        title="Social Media Management"
        description="Social media is vital for connecting with patients and building a community. MITSI manages your social media. We create content that informs, educates, and builds trust with your audience. It encourages interactions to improve patient engagement."
        highlightWords="Management"
        subHeading="How It Engages:"
        subSections={[
          {
            heading: "Increase Patient Engagement",
            description:
              "Engaging posts keep your practice top of mind. They help you build a loyal online community.",
          },
          {
            heading: "Attract Patients",
            description:
              "Your social media can draw in patients seeking your services.",
          },
          {
            heading: "Efficient Booking and Payments",
            description:
              "We link your social media to your scheduling and payment systems for seamless patient conversion.",
          },
        ]}
      />

      <ImageTextSection
        image={section4Image}
        imageAlt="Why MITSI is Ideal Partner"
        imagePosition="left"
        title="Why MITSI is Ideal Partner"
        description="We will integrate your marketing with your practice's systems. This will reduce resistance for your team and your patients. It will ensure a continuous experience."
        highlightWords="Partner"
        subSections={[
          {
            heading: "Here’s what you’ll get when you work with MITSI:",
            listItems: [
              "Smooth Operation: We ensure your marketing and patient systems work together. This creates a smooth experience for all.",
              "Better Data Insights: We align your marketing with patient interactions. This gives clear insights into what's working and where to improve.",
              "Increased Revenue: Our method helps you attract more patients. It also helps you close more appointments and collect payments. This will boost your revenue.",
              "Enhanced Patient Experience: A smooth process, from inquiry to payment, keeps patients satisfied and likely to return.",
            ],
            listItemBoldText: [
              "Smooth Operation:",
              "Better Data Insights:",
              "Increased Revenue:",
              "Enhanced Patient Experience:",
            ],
          },
        ]}
      />
    </>
  );
}
